import update from "immutability-helper";
import ColorGenerator from "libs/color_generator";
import DiffableMap from "libs/diffable_map";
import Toast from "libs/toast";
import { zeroPad } from "libs/utils";
import clamp from "lodash-es/clamp";
import identity from "lodash-es/identity";
import orderBy from "lodash-es/orderBy";
import uniqBy from "lodash-es/uniqBy";
import type { MetadataEntryProto } from "types/api_types";
import { userSettings } from "types/schemas/user_settings.schema";
import { TreeTypeEnum } from "viewer/constants";
import {
  areGeometriesTransformed,
  enforceSkeletonTracing,
  findTreeByNodeId,
  getSkeletonTracing,
  getTree,
  getTreeAndNode,
  getTreesWithType,
} from "viewer/model/accessors/skeletontracing_accessor";
import { AnnotationTool } from "viewer/model/accessors/tool_accessor";
import type { Action } from "viewer/model/actions/actions";
import {
  type SkeletonActionPolicy,
  type SkeletonCollabPolicy,
  type SkeletonTracingAction,
  skeletonActionPolicies,
} from "viewer/model/actions/skeletontracing_actions";
import {
  applyUserStateToGroups,
  convertServerAdditionalAxesToFrontEnd,
  convertServerBoundingBoxToFrontend,
  convertUserBoundingBoxesFromServerToFrontend,
  getApplyUserStateToTreeFn,
} from "viewer/model/reducers/reducer_helpers";
import {
  addTreesAndGroups,
  createBranchPoint,
  createComment,
  createNode,
  createTree,
  createTreeMapFromTreeArray,
  deleteBranchPoint,
  deleteComment,
  deleteEdge,
  deleteNode,
  deleteTrees,
  ensureTreeNames,
  getMaximumNodeId,
  getOrCreateTree,
  mergeTrees,
  removeMissingGroupsFromTrees,
  setExpandedTreeGroups,
  setTreeColorIndex,
  shuffleTreeColor,
  toggleAllTreesReducer,
  toggleTreeGroupReducer,
} from "viewer/model/reducers/skeletontracing_reducer_helpers";
import { type Tree, type TreeGroup, TreeMap } from "viewer/model/types/tree_types";
import type { SkeletonTracing, WebknossosState } from "viewer/store";
import {
  additionallyExpandGroup,
  findGroup,
  GroupTypeEnum,
  getNodeKey,
  MISSING_GROUP_ID,
  mapGroups,
} from "viewer/view/right_border_tabs/shared/tree_hierarchy_view_helpers";
import {
  getUserStateForTracing,
  isAgglomerateTree,
  isConcurrentCollaborationMode,
} from "../accessors/annotation_accessor";
import { max, maxBy } from "../helpers/iterator_utils";
import { applySkeletonUpdateActionsFromServer } from "./update_action_application/skeleton";

// Resolves the existing tree(s) that the given mutation would affect. Only actions classified as
// `collab: "onlyAgglomerateTree"` in `skeletonActionPolicies` are handled here; for those, the
// collab guard allows the mutation only if all affected trees are agglomerate trees.
function resolveAffectedTrees(
  action: Action,
  // The state is needed in addition to the skeletonTracing, because the active tree
  // (which is the fallback for actions without an explicit treeId) lives in localSkeletonState.
  state: WebknossosState,
  skeletonTracing: SkeletonTracing,
): Array<Tree | null | undefined> {
  switch (action.type) {
    case "SET_TREE_NAME":
    case "SET_TREE_METADATA":
    case "SET_TREE_AGGLOMERATE_INFO_ID":
    case "SET_EDGES_ARE_VISIBLE":
    case "SET_TREE_GROUP":
    case "DELETE_TREE":
      return [getTree(state, action.treeId)];
    case "DELETE_TREES":
      return action.treeIds.map((treeId) => getTree(state, treeId));
    case "DELETE_EDGE":
    case "MERGE_TREES":
      // Proofreading dispatches these with initiator "PROOFREADING" on agglomerate trees. The
      // user-initiated variants during proofreading return early in the respective case bodies.
      return [
        findTreeByNodeId(skeletonTracing.trees, action.sourceNodeId),
        findTreeByNodeId(skeletonTracing.trees, action.targetNodeId),
      ];
    case "ADD_TREES_AND_GROUPS":
      // Allowed only when importing agglomerate trees (e.g. the proofreading agglomerate import).
      return Array.from(action.trees.values());
    default:
      // An action not classified as "onlyAgglomerateTree" should never reach this helper.
      return [];
  }
}

// In concurrent collaboration ("live collaboration") mode, normal skeleton editing must be
// forbidden because it would interfere with concurrent edits/rebasing. Only proofreading, which
// operates on agglomerate trees, is allowed. The collab policy comes from `skeletonActionPolicies`.
function isCollabModeMutationAllowed(
  collab: SkeletonCollabPolicy,
  action: Action,
  state: WebknossosState,
  skeletonTracing: SkeletonTracing,
): boolean {
  switch (collab) {
    case "allow":
      return true;
    case "block":
      return false;
    case "onlyAgglomerateTree": {
      const affectedTrees = resolveAffectedTrees(action, state, skeletonTracing);
      return affectedTrees.length > 0 && affectedTrees.every(isAgglomerateTree);
    }
    default: {
      const _exhaustiveCheck: never = collab;
      return false;
    }
  }
}

function SkeletonTracingReducer(
  state: WebknossosState,
  action: Action,
  ignoreAllowUpdate: boolean = false,
): WebknossosState {
  /**
   * INITIALIZE_SKELETONTRACING is handled as a special case here without any
   * permission checks. The action should always be handled and is also the only one
   * that can deal with a not pre-existing skeleton tracing which is why
   * it is not part of the big switch-block below.
   * After that, a permission + collaboration gate guards the mutations in the second switch.
   * Whether an action needs update permission is declared centrally in `skeletonActionPolicies`.
   */
  if (action.type === "INITIALIZE_SKELETONTRACING") {
    const userState = getUserStateForTracing(
      action.tracing,
      state.activeUser,
      state.annotation.owner,
    );

    const applyUserStateToTreeFn = getApplyUserStateToTreeFn(userState);
    const trees = createTreeMapFromTreeArray(action.tracing.trees, applyUserStateToTreeFn);
    let activeNodeId = userState?.activeNodeId ?? action.tracing.activeNodeId;

    const treeGroups = applyUserStateToGroups(action.tracing.treeGroups || [], userState);
    const cachedMaxNodeId = getMaximumNodeId(trees);

    let activeTreeId = null;

    // Find active tree based on active node or pick the last tree
    if (activeNodeId != null) {
      // use activeNodeId to find active tree
      const tree = findTreeByNodeId(trees, activeNodeId);
      if (tree) {
        activeTreeId = tree.treeId;
      } else {
        // There is an activeNodeId without a corresponding tree.
        // Warn the user, since this shouldn't happen, but clear the activeNodeId
        // so that wk is usable.
        Toast.warning(
          `Annotation was initialized with active node ID ${activeNodeId}, which is not present in the trees. Falling back to last tree instead.`,
          {
            timeout: 10000,
          },
        );
        activeNodeId = undefined;
      }
    }

    // If no active tree was found yet, use the last tree
    if (activeTreeId == null) {
      const lastTree = maxBy(trees.values(), "treeId");
      if (lastTree) {
        activeTreeId = lastTree.treeId;
        // use last node for active node
        const lastNode = maxBy(lastTree.nodes.values(), "id");
        activeNodeId = lastNode?.id;
      }
    }

    const userBoundingBoxes = convertUserBoundingBoxesFromServerToFrontend(
      action.tracing.userBoundingBoxes,
      userState,
    );
    const skeletonTracing: SkeletonTracing = {
      createdTimestamp: action.tracing.createdTimestamp,
      type: "skeleton",
      activeNodeId,
      cachedMaxNodeId,
      trees,
      treeGroups,
      tracingId: action.tracing.id,
      boundingBox: convertServerBoundingBoxToFrontend(action.tracing.boundingBox),
      userBoundingBoxes,
      additionalAxes: convertServerAdditionalAxesToFrontEnd(action.tracing.additionalAxes),
    };

    return update(state, {
      annotation: {
        skeleton: {
          $set: skeletonTracing,
        },
        readOnly: {
          $set: null,
        },
      },
      save: {
        rebaseRelevantServerAnnotationState: {
          skeleton: {
            $set: skeletonTracing,
          },
        },
      },
      localSkeletonState: {
        $set: {
          activeTreeId,
          activeGroupId: null,
          navigationList: {
            list: [],
            activeIndex: -1,
          },
          showSkeletons: true,
        },
      },
    });
  }

  const skeletonTracing = getSkeletonTracing(state.annotation);
  if (skeletonTracing == null) {
    return state;
  }

  // Permission + collaboration gate, driven by the action's policy in `skeletonActionPolicies`.
  // Actions without a policy (non-skeleton/saga-only) or without `needsUpdatePermission` skip the
  // gate and are handled below (or ignored via the switch's default).
  const policy: SkeletonActionPolicy | undefined =
    skeletonActionPolicies[action.type as SkeletonTracingAction["type"]];
  // Updates that originate from the server (ignoreAllowUpdate) must still be applied.
  if (!ignoreAllowUpdate && policy?.needsUpdatePermission) {
    // The current action does not originate from the server and needs a permission check.
    const { isUpdatingCurrentlyAllowed } = state.annotation;
    if (!isUpdatingCurrentlyAllowed) {
      // Since editing is not allowed currently (e.g., opened in read-only mode), ignore the action.
      return state;
    }
    // Now, the action may be handled except for when the concurrent collaboration mode
    // is active. In that case, we delegate the check to isCollabModeMutationAllowed
    // which checks that only agglomerate trees may be mutated (because proofreading is
    // currently the only mode that supports live collaboration).
    if (
      isConcurrentCollaborationMode(state) &&
      !isCollabModeMutationAllowed(policy.collab, action, state, skeletonTracing)
    ) {
      return state;
    }
  }

  const { restrictions } = state.annotation;
  switch (action.type) {
    case "SET_ACTIVE_NODE": {
      const { nodeId } = action;

      if (nodeId == null) {
        return update(state, {
          annotation: {
            skeleton: {
              activeNodeId: {
                $set: null,
              },
            },
          },
          localSkeletonState: {
            activeTreeId: {
              $set: null,
            },
            activeGroupId: {
              $set: null,
            },
          },
        });
      }

      const tree = findTreeByNodeId(skeletonTracing.trees, nodeId);
      if (tree) {
        return update(state, {
          annotation: {
            skeleton: {
              activeNodeId: {
                $set: nodeId,
              },
            },
          },
          localSkeletonState: {
            activeTreeId: {
              $set: tree.treeId,
            },
            activeGroupId: {
              $set: null,
            },
          },
        });
      } else {
        return state;
      }
    }

    case "SET_NODE_RADIUS": {
      const { radius, nodeId, treeId } = action;
      const clampedRadius = clamp(
        userSettings.nodeRadius.minimum,
        radius,
        userSettings.nodeRadius.maximum,
      );
      const treeAndNode = getTreeAndNode(
        skeletonTracing,
        state.localSkeletonState.activeTreeId,
        nodeId,
        treeId,
      );
      if (treeAndNode == null) {
        return state;
      }

      const [tree, node] = treeAndNode;
      const diffableMap = skeletonTracing.trees.getOrThrow(tree.treeId).nodes;
      const newDiffableMap = diffableMap.set(
        node.id,
        update(node, {
          radius: {
            $set: clampedRadius,
          },
        }),
      );
      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        nodes: newDiffableMap,
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_SHOW_SKELETONS": {
      const { showSkeletons } = action;
      return update(state, {
        localSkeletonState: {
          showSkeletons: {
            $set: showSkeletons,
          },
        },
      });
    }

    case "SET_ACTIVE_TREE": {
      const { trees } = skeletonTracing;
      const tree = getTree(state, action.treeId);
      if (tree == null) {
        return state;
      }

      const newActiveNodeId = maxBy(trees.getOrThrow(tree.treeId).nodes.values(), "id")?.id || null;
      return update(state, {
        annotation: {
          skeleton: {
            activeNodeId: {
              $set: newActiveNodeId,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: tree.treeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "SET_ACTIVE_TREE_BY_NAME": {
      const { treeName } = action;
      const { trees } = skeletonTracing;

      const treeWithMatchingName = trees.values().find((tree) => tree.name === treeName);

      if (!treeWithMatchingName) {
        return state;
      }

      const newActiveNodeId = max(treeWithMatchingName.nodes.values().map((el) => el.id)) || null;
      return update(state, {
        annotation: {
          skeleton: {
            activeNodeId: {
              $set: newActiveNodeId,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: treeWithMatchingName.treeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "DESELECT_ACTIVE_TREE": {
      return update(state, {
        annotation: {
          skeleton: {
            activeNodeId: {
              $set: null,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: null,
          },
        },
      });
    }

    case "SET_TREE_ACTIVE_GROUP": {
      return update(state, {
        annotation: {
          skeleton: {
            activeNodeId: {
              $set: null,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: null,
          },
          activeGroupId: {
            $set: action.groupId,
          },
        },
      });
    }

    case "DESELECT_ACTIVE_TREE_GROUP": {
      return update(state, {
        localSkeletonState: {
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "SELECT_NEXT_TREE": {
      const { trees } = skeletonTracing;
      const { activeTreeId } = state.localSkeletonState;
      if (trees.size() === 0) return state;
      const increaseDecrease = action.forward ? 1 : -1;
      const orderAttribute = state.userConfiguration.sortTreesByName ? "name" : "timestamp";

      const treeIds = orderBy(trees.values().toArray(), [orderAttribute]).map((t) => t.treeId);

      // default to the first tree
      const activeTreeIdIndex = activeTreeId != null ? treeIds.indexOf(activeTreeId) : 0;
      // treeIds.length is taken into account in this calculation, because -1 % n == -1
      const newActiveTreeIdIndex =
        (activeTreeIdIndex + increaseDecrease + treeIds.length) % treeIds.length;

      const newActiveTreeId = treeIds[newActiveTreeIdIndex];
      const newActiveNodeId = maxBy(trees.getOrThrow(newActiveTreeId).nodes.values(), "id")?.id;

      return update(state, {
        annotation: {
          skeleton: {
            activeNodeId: {
              $set: newActiveNodeId,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: newActiveTreeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "SET_TREE_COLOR_INDEX": {
      const { colorIndex } = action;
      const tree = getTree(state, action.treeId);
      if (tree == null) {
        return state;
      }

      const result = setTreeColorIndex(skeletonTracing, tree, colorIndex);
      if (result == null) {
        return state;
      }
      const [updatedTree, treeId] = result;
      const newTrees = skeletonTracing.trees.set(treeId, updatedTree);

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_TREE_COLOR": {
      const { color, treeId } = action;
      const tree = getTree(state, treeId);
      if (tree == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, { ...tree, color });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SHUFFLE_TREE_COLOR": {
      const tree = getTree(state, action.treeId);
      if (tree == null) {
        return state;
      }

      const result = shuffleTreeColor(skeletonTracing, tree);
      if (result == null) {
        return state;
      }
      const [updatedTree, treeId] = result;
      const newTrees = skeletonTracing.trees.set(treeId, updatedTree);

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_TREE_TYPE": {
      const { treeType, treeId } = action;
      const tree = getTree(state, treeId);
      if (tree == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(treeId, { ...tree, type: treeType });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SHUFFLE_ALL_TREE_COLORS": {
      const newColors = ColorGenerator.getNRandomColors(skeletonTracing.trees.size());

      const newTrees = skeletonTracing.trees.clone();
      for (const tree of skeletonTracing.trees.values()) {
        // @ts-expect-error newColors.shift() can be undefined
        newTrees.mutableSet(tree.treeId, { ...tree, color: newColors.shift() });
      }

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_SKELETON_TRACING": {
      return update(state, {
        annotation: {
          skeleton: {
            $set: action.tracing,
          },
        },
      });
    }

    case "TOGGLE_TREE": {
      const { treeId } = action;
      const tree = getTree(state, treeId);
      if (tree == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        isVisible: !tree.isVisible,
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_TREE_VISIBILITY": {
      const { treeId, isVisible } = action;
      const tree = getTree(state, treeId);
      if (tree == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, { ...tree, isVisible });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_EXPANDED_TREE_GROUPS_BY_KEYS": {
      const { expandedGroups } = action;

      return setExpandedTreeGroups(state, (group: TreeGroup) =>
        expandedGroups.has(getNodeKey(GroupTypeEnum.GROUP, group.groupId)),
      );
    }

    case "SET_EXPANDED_TREE_GROUPS_BY_IDS": {
      const { expandedGroups } = action;

      return setExpandedTreeGroups(state, (group: TreeGroup) => expandedGroups.has(group.groupId));
    }

    case "EXPAND_PARENT_GROUPS_OF_TREE": {
      const { tree } = action;
      if (tree.groupId == null || skeletonTracing == null) {
        return state;
      }
      const expandedGroups = additionallyExpandGroup(
        skeletonTracing.treeGroups,
        tree.groupId,
        identity,
      );
      if (expandedGroups == null) {
        return state;
      }
      return setExpandedTreeGroups(state, (group: TreeGroup) => expandedGroups.has(group.groupId));
    }

    case "TOGGLE_ALL_TREES": {
      return toggleAllTreesReducer(state, skeletonTracing);
    }

    case "TOGGLE_INACTIVE_TREES": {
      const { activeGroupId } = state.localSkeletonState;

      if (activeGroupId != null) {
        // Toggle all trees
        const toggledTreeState = toggleAllTreesReducer(state, skeletonTracing);

        if (toggledTreeState.annotation.skeleton == null) {
          throw new Error("Satisfy typescript");
        }

        // Ensure the active group is visible
        return toggleTreeGroupReducer(
          toggledTreeState,
          toggledTreeState.annotation.skeleton,
          activeGroupId,
          true,
        );
      }

      const activeTree = getTree(state);
      if (activeTree == null) {
        return state;
      }

      const toggledTreeState = toggleAllTreesReducer(state, skeletonTracing);
      if (toggledTreeState.annotation.skeleton == null) {
        // Skeleton should not have become null in the meantime
        throw new Error("Skeleton must not be null");
      }
      const newTrees = toggledTreeState.annotation.skeleton.trees.set(activeTree.treeId, {
        ...activeTree,
        isVisible: true,
      });

      return update(toggledTreeState, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "TOGGLE_TREE_GROUP":
      return toggleTreeGroupReducer(state, skeletonTracing, action.groupId);

    case "UPDATE_NAVIGATION_LIST": {
      const { list, activeIndex } = action;
      return update(state, {
        localSkeletonState: {
          navigationList: {
            list: {
              $set: list,
            },
            activeIndex: {
              $set: activeIndex,
            },
          },
        },
      });
    }

    case "SET_MERGER_MODE_ENABLED": {
      const { active } = action;
      return update(state, {
        temporaryConfiguration: {
          isMergerModeEnabled: {
            $set: active,
          },
        },
      });
    }

    case "APPLY_SKELETON_UPDATE_ACTIONS_FROM_SERVER": {
      const { actions } = action;
      return applySkeletonUpdateActionsFromServer(
        // Pass a SkeletonTracingReducer that ignores allowUpdate because
        // we want to be able to apply updates even in read-only views.
        (state: WebknossosState, action: Action) => SkeletonTracingReducer(state, action, true),
        actions,
        state,
        action.ignoreUnsupportedActionTypes,
      );
    }

    case "CREATE_NODE": {
      if (areGeometriesTransformed(state)) {
        // Don't create nodes if the skeleton layer is rendered with transforms.
        return state;
      }

      const { position, rotation, viewport, mag, treeId, timestamp, additionalCoordinates } =
        action;
      const tree = getOrCreateTree(state, skeletonTracing, treeId, timestamp, TreeTypeEnum.DEFAULT);
      if (tree == null) {
        return state;
      }

      const nodeResult = createNode(
        state,
        skeletonTracing,
        tree,
        position,
        additionalCoordinates,
        rotation,
        viewport,
        mag,
        timestamp,
      );
      if (nodeResult == null) {
        return state;
      }

      const [node, edges] = nodeResult;
      const diffableNodeMap = tree.nodes;
      const newDiffableMap = diffableNodeMap.set(node.id, node);
      const newTree = update(tree, {
        nodes: {
          $set: newDiffableMap,
        },
        edges: {
          $set: edges,
        },
      });
      const activeNodeId = action.dontActivate ? skeletonTracing.activeNodeId : node.id;
      const activeTreeId = action.dontActivate
        ? state.localSkeletonState.activeTreeId
        : tree.treeId;

      const newTrees = skeletonTracing.trees.set(tree.treeId, newTree);

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
            activeNodeId: {
              $set: activeNodeId,
            },
            cachedMaxNodeId: {
              $set: node.id,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: activeTreeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "DELETE_NODE": {
      const { timestamp, nodeId, treeId } = action;
      const treeAndNode = getTreeAndNode(
        skeletonTracing,
        state.localSkeletonState.activeTreeId,
        nodeId,
        treeId,
        TreeTypeEnum.DEFAULT,
      );
      if (treeAndNode == null) {
        return state;
      }

      const [tree, node] = treeAndNode;
      const deleteResult = deleteNode(state, tree, node, timestamp);
      if (deleteResult == null) {
        return state;
      }

      const [trees, newActiveTreeId, newActiveNodeId, newMaxNodeId] = deleteResult;
      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: trees,
            },
            activeNodeId: {
              $set: newActiveNodeId,
            },
            cachedMaxNodeId: {
              $set: newMaxNodeId,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: newActiveTreeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "DELETE_EDGE": {
      const { timestamp, sourceNodeId, targetNodeId } = action;

      if (sourceNodeId === targetNodeId) {
        return state;
      }
      const isProofreadingActive = state.uiInformation.activeTool === AnnotationTool.PROOFREAD;
      if (isProofreadingActive && action.initiator === "USER") {
        // Ignore this action as proofreading is active and the action originates from the user.
        // The proofreading saga will take care of replaying the same action but with initiator = "PROOFREADING"
        // to perform the desired tree manipulation.
        return state;
      }

      const treeType = isProofreadingActive ? TreeTypeEnum.AGGLOMERATE : TreeTypeEnum.DEFAULT;

      const sourceTree = getTreeAndNode(
        skeletonTracing,
        state.localSkeletonState.activeTreeId,
        sourceNodeId,
        null,
        treeType,
      );
      const targetTree = getTreeAndNode(
        skeletonTracing,
        state.localSkeletonState.activeTreeId,
        targetNodeId,
        null,
        treeType,
      );
      if (sourceTree == null || targetTree == null) {
        return state;
      }

      const [sourceTreeObj, sourceNode] = sourceTree;
      const [targetTreeObj, targetNode] = targetTree;

      const deleteEdgeResult = deleteEdge(
        state,
        sourceTreeObj,
        sourceNode,
        targetTreeObj,
        targetNode,
        timestamp,
      );
      if (deleteEdgeResult == null) {
        return state;
      }

      const [trees, newActiveTreeId] = deleteEdgeResult;
      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: trees,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: newActiveTreeId,
          },
        },
      });
    }

    case "SET_NODE_POSITION": {
      if (areGeometriesTransformed(state)) {
        // Don't move node if the skeleton layer is rendered with transforms.
        return state;
      }
      const { position, nodeId, treeId } = action;
      const treeAndNode = getTreeAndNode(
        skeletonTracing,
        state.localSkeletonState.activeTreeId,
        nodeId,
        treeId,
        TreeTypeEnum.DEFAULT,
      );
      if (treeAndNode == null) {
        return state;
      }

      const [tree, node] = treeAndNode;
      const nodesMap = skeletonTracing.trees.getOrThrow(tree.treeId).nodes;
      const newNodesMap = nodesMap.set(
        node.id,
        update(node, {
          untransformedPosition: {
            // Don't round here, since this would make the continuous
            // movement of a node weird.
            $set: position,
          },
        }),
      );

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        nodes: newNodesMap,
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "CREATE_BRANCHPOINT": {
      const { timestamp, nodeId, treeId } = action;
      const treeAndNode = getTreeAndNode(
        skeletonTracing,
        state.localSkeletonState.activeTreeId,
        nodeId,
        treeId,
      );
      if (treeAndNode == null) {
        return state;
      }

      const [tree, node] = treeAndNode;
      const branchPoint = createBranchPoint(tree, node, timestamp, restrictions);
      if (branchPoint == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        branchPoints: [...tree.branchPoints, branchPoint],
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "DELETE_BRANCHPOINT": {
      const branchPointResult = deleteBranchPoint(skeletonTracing, restrictions);
      if (branchPointResult == null) {
        return state;
      }
      const [branchPoints, treeId, newActiveNodeId] = branchPointResult;
      const tree = skeletonTracing.trees.getOrThrow(treeId);
      const newTrees = skeletonTracing.trees.set(treeId, {
        ...tree,
        branchPoints,
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
            activeNodeId: {
              $set: newActiveNodeId,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: treeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "DELETE_BRANCHPOINT_BY_ID": {
      const { nodeId, treeId } = action;
      const tree = getTree(state, treeId);
      if (tree == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        branchPoints: tree.branchPoints.filter((bp: any) => bp.nodeId !== nodeId),
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "CREATE_TREE": {
      const { timestamp } = action;
      const tree = createTree(state, timestamp);
      if (tree == null) {
        return state;
      }

      if (action.treeIdCallback) {
        action.treeIdCallback(tree.treeId);
      }
      const newTrees = skeletonTracing.trees.set(tree.treeId, tree);

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
            activeNodeId: {
              $set: null,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: tree.treeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "ADD_TREES_AND_GROUPS": {
      const { trees, treeGroups, assignNewGroupId, targetGroupId } = action;
      const treesWithNames = ensureTreeNames(state, trees);
      const treesResult = addTreesAndGroups(
        skeletonTracing,
        treesWithNames,
        treeGroups,
        assignNewGroupId,
        targetGroupId,
      );
      if (treesResult == null) {
        return state;
      }

      const [updatedTrees, updatedTreeGroups, newMaxNodeId] = treesResult;
      if (action.treeIdsCallback) {
        action.treeIdsCallback(
          updatedTrees
            .values()
            .map((tree) => tree.treeId)
            .toArray(),
        );
      }

      const newTrees = DiffableMap.merge(skeletonTracing.trees, updatedTrees);
      // Nest the newly added groups into the selected target group (if it still exists),
      // falling back to adding them at the root of the hierarchy otherwise.
      const targetGroupExists =
        targetGroupId !== MISSING_GROUP_ID &&
        findGroup(skeletonTracing.treeGroups, targetGroupId) != null;
      const newTreeGroups = targetGroupExists
        ? mapGroups(skeletonTracing.treeGroups, (group) =>
            group.groupId === targetGroupId
              ? { ...group, children: group.children.concat(updatedTreeGroups) }
              : group,
          )
        : skeletonTracing.treeGroups.concat(updatedTreeGroups);

      return update(state, {
        annotation: {
          skeleton: {
            treeGroups: {
              $set: newTreeGroups,
            },
            trees: {
              $set: newTrees,
            },
            cachedMaxNodeId: {
              $set: newMaxNodeId,
            },
          },
        },
      });
    }

    case "DELETE_TREE":
    case "DELETE_TREES": {
      const { suppressActivatingNextNode } = action;
      let treeIds: number[] = [];

      if (action.type === "DELETE_TREE") {
        const tree = getTree(state, action.treeId);
        if (tree != null) {
          treeIds = [tree.treeId];
        }
      } else {
        treeIds = action.treeIds;
      }

      const deleteResult = deleteTrees(skeletonTracing, treeIds, suppressActivatingNextNode);
      if (deleteResult == null) {
        return state;
      }

      const [trees, newActiveTreeId, newActiveNodeId, newMaxNodeId] = deleteResult;
      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: trees,
            },
            activeNodeId: {
              $set: newActiveNodeId,
            },
            cachedMaxNodeId: {
              $set: newMaxNodeId,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: newActiveTreeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "RESET_SKELETON_TRACING": {
      const newTree = createTree(state, Date.now(), false);
      if (newTree == null) {
        return state;
      }

      const newTreeMap: TreeMap = new TreeMap([[newTree.treeId, newTree]]);

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTreeMap,
            },
            activeNodeId: {
              $set: null,
            },
            treeGroups: {
              $set: [],
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: newTree.treeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "MERGE_TREES": {
      const { sourceNodeId, targetNodeId } = action;
      const isProofreadingActive = state.uiInformation.activeTool === AnnotationTool.PROOFREAD;
      if (isProofreadingActive && action.initiator === "USER") {
        // Ignore this action as proofreading is active and the action originates from the user.
        // The proofreading saga will take care of replaying the same action but with initiator = "PROOFREADING"
        // to perform the desired tree manipulation.
        return state;
      }

      const treeType = isProofreadingActive ? TreeTypeEnum.AGGLOMERATE : TreeTypeEnum.DEFAULT;
      const oldTrees = skeletonTracing.trees;
      const mergeResult = mergeTrees(oldTrees, sourceNodeId, targetNodeId, treeType);

      if (mergeResult == null) {
        return state;
      }
      const [trees, newActiveTreeId, newActiveNodeId] = mergeResult;

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: trees,
            },
            activeNodeId: {
              $set: newActiveNodeId,
            },
          },
        },
        localSkeletonState: {
          activeTreeId: {
            $set: newActiveTreeId,
          },
          activeGroupId: {
            $set: null,
          },
        },
      });
    }

    case "SET_TREE_NAME": {
      const tree = getTree(state, action.treeId);
      if (tree == null) {
        return state;
      }

      const defaultName = `Tree${zeroPad(tree.treeId, 3)}`;
      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        name: action.name || defaultName,
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_TREE_METADATA": {
      const tree = getTree(state, action.treeId);
      if (tree == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        metadata: sanitizeMetadata(action.metadata),
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_TREE_AGGLOMERATE_INFO_ID": {
      const tree = getTree(state, action.treeId);
      if (tree == null || tree.agglomerateInfo == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        agglomerateInfo: { ...tree.agglomerateInfo, agglomerateId: action.agglomerateId },
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_TREES_AGGLOMERATE_INFO_TRACING_ID": {
      const agglomerateTrees = getTreesWithType(
        enforceSkeletonTracing(state.annotation),
        TreeTypeEnum.AGGLOMERATE,
      );
      const newTrees = agglomerateTrees
        .values()
        .reduce((currentTrees: TreeMap, agglomerateTree: Tree) => {
          const updatedTree =
            agglomerateTree.agglomerateInfo != null
              ? {
                  ...agglomerateTree,
                  agglomerateInfo: {
                    agglomerateId: agglomerateTree.agglomerateInfo.agglomerateId,
                    tracingId: action.newAgglomerateMappingTracingId,
                    mappingId: null,
                  },
                }
              : agglomerateTree;
          return currentTrees.set(agglomerateTree.treeId, updatedTree);
        }, skeletonTracing.trees);

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_EDGES_ARE_VISIBLE": {
      const tree = getTree(state, action.treeId);
      if (tree == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        edgesAreVisible: action.edgesAreVisible,
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "CREATE_COMMENT": {
      const { commentText, nodeId, treeId } = action;
      const treeAndNode = getTreeAndNode(
        skeletonTracing,
        state.localSkeletonState.activeTreeId,
        nodeId,
        treeId,
      );
      if (treeAndNode == null) {
        return state;
      }

      const [tree, node] = treeAndNode;
      const comments = createComment(skeletonTracing, tree, node, commentText);
      if (comments == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        comments,
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "DELETE_COMMENT": {
      const treeAndNode = getTreeAndNode(
        skeletonTracing,
        state.localSkeletonState.activeTreeId,
        action.nodeId,
        action.treeId,
      );
      if (treeAndNode == null) {
        return state;
      }

      const [tree, node] = treeAndNode;
      const comments = deleteComment(skeletonTracing, tree, node);
      if (comments == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        comments,
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_TREE_GROUPS": {
      const { treeGroups } = action;

      const updatedTrees = removeMissingGroupsFromTrees(skeletonTracing, treeGroups);
      const newTrees = DiffableMap.merge(skeletonTracing.trees, updatedTrees);

      return update(state, {
        annotation: {
          skeleton: {
            treeGroups: {
              $set: action.treeGroups,
            },
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    case "SET_TREE_GROUP": {
      const { treeId, groupId } = action;
      const tree = getTree(state, treeId);
      if (tree == null) {
        return state;
      }

      const newTrees = skeletonTracing.trees.set(tree.treeId, {
        ...tree,
        groupId,
      });

      return update(state, {
        annotation: {
          skeleton: {
            trees: {
              $set: newTrees,
            },
          },
        },
      });
    }

    default:
      return state;
  }
}

export function sanitizeMetadata(metadata: MetadataEntryProto[]) {
  // Workaround for stringList values that are [], even though they
  // should be null. This workaround is necessary because protobuf cannot
  // distinguish between an empty list and a not existent property.
  // Therefore, we clean this up here.
  // Additionally, we ensure uniqueness of the metadata keys just to be
  // extra safe. Actually, duplicate keys should never be saved to the back-end,
  // as duplicate keys will collapse during diffing and only one of the entries
  // will survive.
  return uniqBy(
    metadata.map((prop) => {
      // If stringList value is defined, but it's an empty array, it should
      // be switched to undefined
      const needsCorrection =
        prop.stringListValue != null &&
        prop.stringListValue.length === 0 &&
        (prop.stringValue != null || prop.numberValue != null || prop.boolValue != null);
      if (needsCorrection) {
        return {
          ...prop,
          stringListValue: undefined,
        };
      }
      return prop;
    }),
    (entry) => entry.key,
  );
}

export default SkeletonTracingReducer;
